import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { PushNotifications, PushNotificationSchema, Token, ActionPerformed } from '@capacitor/push-notifications';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Storage } from '@ionic/storage-angular';
import { Router } from '@angular/router';
import { ToastController, AlertController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  
  private apiUrl = environment.apiUrl;
  private fcmToken = new BehaviorSubject<string | null>(null);
  private notificationPermissionStatus = new BehaviorSubject<string>('prompt');
  private badgeCountSubject = new BehaviorSubject<number>(0);
  private isInitialized = false;

  constructor(
    private platform: Platform,
    private http: HttpClient,
    private storage: Storage,
    private router: Router,
    private toastController: ToastController,
    private alertController: AlertController
  ) {
    this.initializeStorage();
  }

  private async initializeStorage() {
    await this.storage.create();
  }

  /**
   * Initialize push notifications
   * Should be called after user login or on app startup if user is logged in
   */
  async initialize() {
    console.log('🚀 [PUSH] initialize() called');
    if (this.isInitialized) {
      console.log('🚀 [PUSH] Push notifications already initialized');
      // Try to re-register if we have a token but haven't registered yet
      await this.ensureRegistration();
      return;
    }

    // Only initialize on native platforms
    const platform = Capacitor.getPlatform();
    console.log('🚀 [PUSH] Current platform:', platform);
    
    if (platform === 'web') {
      console.log('🚀 [PUSH] Push notifications not available on web');
      return;
    }

    try {
      console.log('🚀 [PUSH] Starting permission request...');
      // Request permission
      const permissionResult = await this.requestPermission();
      console.log('🚀 [PUSH] Permission result:', permissionResult);
      if (!permissionResult) {
        console.log('🚀 [PUSH] Push notification permission denied');
        return;
      }

      console.log('🚀 [PUSH] Permission granted, registering with FCM...');
      // Register with FCM
      await this.register();
      console.log('🚀 [PUSH] FCM registration called');

      console.log('🚀 [PUSH] Setting up listeners...');
      // Set up listeners
      this.setupListeners();
      console.log('🚀 [PUSH] Listeners set up');

      this.isInitialized = true;
      console.log('🚀 [PUSH] Push notifications initialized successfully');
      
      // Ensure registration after initialization
      await this.ensureRegistration();
    } catch (error) {
      console.error('🚀 [PUSH] Error initializing push notifications:', error);
    }
  }
  
  /**
   * Ensure device token is registered with backend
   * Can be called manually to retry registration
   */
  async ensureRegistration() {
    console.log('🚀 [PUSH] ensureRegistration() called');
    
    // Check if we have a stored token
    const storedToken = await this.storage.get('fcm_token');
    const isRegistered = await this.storage.get('fcm_registered');
    
    if (storedToken && !isRegistered) {
      console.log('🚀 [PUSH] Found unregistered token, attempting registration...');
      await this.registerDeviceWithBackend(storedToken);
    } else if (storedToken && isRegistered) {
      console.log('🚀 [PUSH] Token already registered');
    } else {
      console.log('🚀 [PUSH] No stored token found');
      
      // Try to get current token
      const currentToken = this.fcmToken.getValue();
      if (currentToken) {
        console.log('🚀 [PUSH] Using current token from BehaviorSubject');
        await this.registerDeviceWithBackend(currentToken);
      }
    }
  }

  /**
   * Request permission for push notifications
   */
  async requestPermission(): Promise<boolean> {
    console.log('🚀 [PUSH] requestPermission() called');
    try {
      const permission = await PushNotifications.requestPermissions();
      console.log('🚀 [PUSH] Permission status:', permission);
      
      if (permission.receive === 'granted') {
        this.notificationPermissionStatus.next('granted');
        return true;
      } else if (permission.receive === 'denied') {
        this.notificationPermissionStatus.next('denied');
        
        // Show alert explaining why notifications are important
        const alert = await this.alertController.create({
          header: 'Enable Notifications',
          message: 'Get notified when someone finds your duck, sends you a message, or when there are special offers!',
          buttons: [
            {
              text: 'Not Now',
              role: 'cancel'
            },
            {
              text: 'Open Settings',
              handler: () => {
                // On iOS, this would open app settings
                // Implementation depends on platform
                console.log('Open app settings');
              }
            }
          ]
        });
        await alert.present();
        
        return false;
      } else {
        this.notificationPermissionStatus.next('prompt');
        return false;
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      return false;
    }
  }

  /**
   * Register for push notifications
   */
  async register() {
    console.log('🚀 [PUSH] register() called');
    try {
      await PushNotifications.register();
      console.log('🚀 [PUSH] PushNotifications.register() completed');
    } catch (error) {
      console.error('🚀 [PUSH] Error registering push notifications:', error);
    }
  }

  /**
   * Set up push notification listeners
   */
  private setupListeners() {
    console.log('🚀 [PUSH] setupListeners() called');
    // Listen for registration token
    PushNotifications.addListener('registration', async (token: Token) => {
      console.log('🚀 [PUSH] 🎉 FCM TOKEN RECEIVED! Value: ' + token.value);
      this.fcmToken.next(token.value);
      
      console.log('🚀 [PUSH] Sending token to backend...');
      // Send token to backend
      await this.registerDeviceWithBackend(token.value);
      console.log('🚀 [PUSH] Token sent to backend');
      
      // Store token locally
      await this.storage.set('fcm_token', token.value);
      console.log('🚀 [PUSH] Token stored locally');
    });

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('🚀 [PUSH] ❌ FCM REGISTRATION ERROR: ' + JSON.stringify(error));
    });

    // Listen for push notifications received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', 
      async (notification: PushNotificationSchema) => {
        console.log('Push notification received: ' + JSON.stringify(notification));
        
        // Show in-app notification
        await this.showInAppNotification(notification);
        
        // Update badge count if provided
        if (notification.badge) {
          this.updateBadge(parseInt(notification.badge.toString()));
        }
      }
    );

    // Listen for push notification actions (tap on notification)
    PushNotifications.addListener('pushNotificationActionPerformed', 
      async (notification: ActionPerformed) => {
        console.log('Push notification action performed: ' + JSON.stringify(notification));
        
        // Handle navigation based on notification data
        await this.handleNotificationAction(notification);
        
        // Track click with backend
        if (notification.notification.data?.notificationId) {
          await this.trackNotificationClick(
            notification.notification.data.notificationId,
            notification.actionId
          );
        }
      }
    );
  }

  /**
   * Register device token with backend
   */
  private async registerDeviceWithBackend(token: string, retryCount: number = 0) {
    console.log('🚀 [PUSH] registerDeviceWithBackend() called with token:', token.substring(0, 20) + '...');
    
    // Check if user is authenticated first
    const userData = localStorage.getItem('user');
    if (!userData) {
      console.warn('🚀 [PUSH] ⚠️ No user data found, delaying registration for 2 seconds...');
      if (retryCount < 3) {
        setTimeout(() => {
          this.registerDeviceWithBackend(token, retryCount + 1);
        }, 2000);
      } else {
        console.error('🚀 [PUSH] ❌ Failed to register after 3 retries - no user data');
      }
      return;
    }
    
    const user = JSON.parse(userData);
    if (!user.accessToken) {
      console.warn('🚀 [PUSH] ⚠️ No access token found, delaying registration for 2 seconds...');
      if (retryCount < 3) {
        setTimeout(() => {
          this.registerDeviceWithBackend(token, retryCount + 1);
        }, 2000);
      } else {
        console.error('🚀 [PUSH] ❌ Failed to register after 3 retries - no access token');
      }
      return;
    }
    
    console.log('🚀 [PUSH] ✅ User authenticated, proceeding with registration');
    console.log('🚀 [PUSH] User has token:', user.accessToken ? 'Yes' : 'No');
    
    try {
      const platform = this.platform.is('ios') ? 'ios' : 'android';
      console.log('🚀 [PUSH] Detected platform:', platform);
      console.log('🚀 [PUSH] API URL:', this.apiUrl);
      
      const payload = {
        token: token,
        platform: platform
      };
      console.log('🚀 [PUSH] Sending payload:', payload);
      
      // Using toPromise for RxJS 6 compatibility
      const result = await this.http.post(`${this.apiUrl}/api/notifications/register`, payload).toPromise();
      
      console.log('🚀 [PUSH] ✅ Backend registration response:', result);
      console.log('🚀 [PUSH] Device registered with backend successfully');
      
      // Store registration success flag
      await this.storage.set('fcm_registered', true);
      await this.storage.set('fcm_registration_date', new Date().toISOString());
      
    } catch (error: any) {
      console.error('🚀 [PUSH] ❌ Error registering device with backend:', error);
      console.error('🚀 [PUSH] Error details:', {
        status: error?.status,
        message: error?.message,
        error: error?.error
      });
      
      // Retry logic for network errors
      if (error?.status === 0 || error?.status >= 500) {
        if (retryCount < 3) {
          const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
          console.log(`🚀 [PUSH] 🔄 Retrying registration in ${delay}ms (attempt ${retryCount + 1}/3)`);
          setTimeout(() => {
            this.registerDeviceWithBackend(token, retryCount + 1);
          }, delay);
        } else {
          console.error('🚀 [PUSH] ❌ Max retries reached, registration failed');
        }
      } else if (error?.status === 401) {
        console.error('🚀 [PUSH] ❌ Authentication error - token might be expired');
        // The interceptor should handle token refresh
      }
    }
  }

  /**
   * Show in-app notification (when app is in foreground)
   */
  private async showInAppNotification(notification: PushNotificationSchema) {
    console.log('🚀 [PUSH] showInAppNotification called for:', notification.title);
    
    // Store notification locally for history
    await this.storeNotificationLocally(notification);
    
    // Update badge count and notify all subscribers
    await this.updateBadgeCountAndNotify();
    
    // Show toast for immediate feedback with longer duration
    const toast = await this.toastController.create({
      header: notification.title || 'New Notification',
      message: notification.body || '',
      position: 'top',
      duration: 6000, // Increased from 4000 to 6000ms
      cssClass: 'custom-notification-toast',
      buttons: [
        {
          text: 'View',
          handler: () => {
            this.handleNotificationAction({
              notification: notification,
              actionId: 'tap'
            } as ActionPerformed);
          }
        },
        {
          text: 'Dismiss',
          role: 'cancel'
        }
      ]
    });
    
    await toast.present();
    
    // Log for debugging
    console.log('🚀 [PUSH] Foreground notification shown and stored');
  }
  
  /**
   * Store notification locally for history/inbox feature
   */
  private async storeNotificationLocally(notification: PushNotificationSchema) {
    try {
      // Filter out badge update notifications
      if (this.isBadgeUpdateNotification(notification)) {
        console.log('🚀 [PUSH] Skipping badge update notification from history');
        return;
      }
      
      // Get existing notifications from storage
      const stored = await Preferences.get({ key: 'notification_history' });
      let notifications = stored.value ? JSON.parse(stored.value) : [];
      
      // Add new notification with timestamp
      notifications.unshift({
        ...notification,
        receivedAt: new Date().toISOString(),
        read: false,
        id: Date.now().toString()
      });
      
      // Keep only last 50 notifications
      if (notifications.length > 50) {
        notifications = notifications.slice(0, 50);
      }
      
      // Save back to storage
      await Preferences.set({
        key: 'notification_history',
        value: JSON.stringify(notifications)
      });
      
      console.log('🚀 [PUSH] Notification stored in history');
    } catch (error) {
      console.error('🚀 [PUSH] Error storing notification:', error);
    }
  }

  /**
   * Get notification history
   */
  public async getNotificationHistory(): Promise<any[]> {
    try {
      const stored = await Preferences.get({ key: 'notification_history' });
      const notifications = stored.value ? JSON.parse(stored.value) : [];
      // Filter out badge update notifications from existing history
      return notifications.filter((n: any) => !this.isBadgeUpdateNotification(n));
    } catch (error) {
      console.error('🚀 [PUSH] Error getting notification history:', error);
      return [];
    }
  }
  
  /**
   * Get observable for badge count updates
   */
  public getBadgeCountObservable() {
    return this.badgeCountSubject.asObservable();
  }
  
  /**
   * Get current badge count
   */
  public getCurrentBadgeCount(): number {
    return this.badgeCountSubject.getValue();
  }
  
  /**
   * Update badge count and notify subscribers
   */
  public async updateBadgeCountAndNotify() {
    try {
      const notifications = await this.getNotificationHistory();
      const unreadCount = notifications.filter(n => !n.read).length;
      console.log('🚀 [PUSH] Updating badge count to:', unreadCount);
      this.badgeCountSubject.next(unreadCount);
      return unreadCount;
    } catch (error) {
      console.error('🚀 [PUSH] Error updating badge count:', error);
      return 0;
    }
  }
  
  /**
   * Mark notification as read
   */
  public async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const stored = await Preferences.get({ key: 'notification_history' });
      let notifications = stored.value ? JSON.parse(stored.value) : [];
      
      // Find and mark notification as read
      notifications = notifications.map((n: any) => {
        if (n.id === notificationId) {
          n.read = true;
        }
        return n;
      });
      
      // Save back to storage
      await Preferences.set({
        key: 'notification_history',
        value: JSON.stringify(notifications)
      });
      
      // Update badge count and notify all subscribers
      await this.updateBadgeCountAndNotify();
      
      console.log('🚀 [PUSH] Notification marked as read:', notificationId);
    } catch (error) {
      console.error('🚀 [PUSH] Error marking notification as read:', error);
    }
  }
  
  /**
   * Clear notification history
   */
  public async clearNotificationHistory(): Promise<void> {
    try {
      await Preferences.remove({ key: 'notification_history' });
      console.log('🚀 [PUSH] Notification history cleared');
    } catch (error) {
      console.error('🚀 [PUSH] Error clearing notification history:', error);
    }
  }

  /**
   * Handle notification action (navigation)
   */
  private async handleNotificationAction(notification: ActionPerformed) {
    const data = notification.notification.data;
    
    if (!data) {
      return;
    }

    // Handle different notification types
    switch (data.type) {
      case 'ducking':
        if (data.duckingId) {
          await this.router.navigate(['/ducking', data.duckingId]);
        } else {
          await this.router.navigate(['/my-duckings']);
        }
        break;
        
      case 'message':
        if (data.conversationId) {
          await this.router.navigate(['/messages', data.conversationId]);
        } else {
          await this.router.navigate(['/messages']);
        }
        break;
        
      case 'social':
        if (data.socialType === 'like' || data.socialType === 'comment') {
          await this.router.navigate(['/feed']);
        } else if (data.socialType === 'follow') {
          await this.router.navigate(['/profile', data.userId]);
        }
        break;
        
      case 'order':
        if (data.orderId) {
          await this.router.navigate(['/orders', data.orderId]);
        } else {
          await this.router.navigate(['/orders']);
        }
        break;
        
      case 'marketing':
        if (data.action === 'view_cart') {
          await this.router.navigate(['/shopping-cart']);
        } else if (data.couponId) {
          await this.router.navigate(['/coupons']);
        }
        break;
        
      case 'system':
        // Handle system notifications (announcements, etc.)
        if (data.action === 'view_announcement') {
          await this.router.navigate(['/announcements']);
        }
        break;
        
      default:
        // Default navigation to home
        await this.router.navigate(['/home']);
    }
  }

  /**
   * Track notification click with backend
   */
  private async trackNotificationClick(notificationId: string, action: string) {
    try {
      await this.http.post(`${this.apiUrl}/api/notifications/${notificationId}/click`, {
        action: action
      }).toPromise();
    } catch (error) {
      console.error('Error tracking notification click:', error);
    }
  }

  /**
   * Update app badge count
   */
  private updateBadge(count: number) {
    // This would require additional Capacitor plugin for badge management
    // For example: @capacitor/badge
    console.log('Update badge count:', count);
    this.storage.set('badge_count', count);
  }

  /**
   * Get current badge count from server
   */
  async getBadgeCount(): Promise<number> {
    try {
      const response = await this.http.get<any>(`${this.apiUrl}/api/notifications/badge-count`).toPromise();
      if (response?.success) {
        return response.badgeCount || 0;
      }
      return 0;
    } catch (error) {
      console.error('Error getting badge count:', error);
      return 0;
    }
  }

  /**
   * Clear badge count (mark all notifications as read)
   */
  async clearBadgeCount(): Promise<boolean> {
    try {
      const response = await this.http.post<any>(`${this.apiUrl}/api/notifications/clear-badge`, {}).toPromise();
      if (response?.success) {
        await this.storage.set('badge_count', 0);
        console.log('Badge count cleared successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error clearing badge count:', error);
      return false;
    }
  }

  /**
   * Update badge count on server and device
   */
  async updateBadgeCountManually(count: number): Promise<boolean> {
    try {
      const response = await this.http.post<any>(`${this.apiUrl}/api/notifications/update-badge`, { count }).toPromise();
      if (response?.success) {
        await this.storage.set('badge_count', count);
        console.log(`Badge count updated to: ${count}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating badge count:', error);
      return false;
    }
  }

  /**
   * Get local badge count from storage
   */
  async getLocalBadgeCount(): Promise<number> {
    try {
      const count = await this.storage.get('badge_count');
      return count || 0;
    } catch (error) {
      console.error('Error getting local badge count:', error);
      return 0;
    }
  }
  
  /**
   * Check if notification is a badge update notification
   */
  private isBadgeUpdateNotification(notification: any): boolean {
    if (!notification) return false;
    
    // Check various indicators that this is a badge update notification
    const title = (notification.title || '').toLowerCase();
    const body = (notification.body || '').toLowerCase();
    const type = notification.data?.type?.toLowerCase() || '';
    
    // Common patterns for badge update notifications
    const badgePatterns = [
      'badge update',
      'badge count',
      'unread count',
      'notification count',
      'new notifications:'
    ];
    
    return badgePatterns.some(pattern => 
      title.includes(pattern) || 
      body.includes(pattern) || 
      type.includes('badge')
    ) || type === 'badge_update';
  }

  /**
   * Get current FCM token
   */
  getFcmToken() {
    return this.fcmToken.asObservable();
  }

  /**
   * Get permission status
   */
  getPermissionStatus() {
    return this.notificationPermissionStatus.asObservable();
  }

  /**
   * Update notification preferences
   */
  async updatePreferences(preferences: any) {
    try {
      const response = await this.http.put(`${this.apiUrl}/api/notifications/preferences`, preferences).toPromise();
      console.log('Preferences updated:', response);
      
      // Store preferences locally
      await this.storage.set('notification_preferences', preferences);
      
      return response;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }

  /**
   * Get notification preferences
   */
  async getPreferences() {
    try {
      const response = await this.http.get(`${this.apiUrl}/notifications/preferences`).toPromise();
      return response;
    } catch (error) {
      console.error('Error getting preferences:', error);
      // Return cached preferences if available
      const cached = await this.storage.get('notification_preferences');
      return cached || {};
    }
  }

  /**
   * Subscribe to topic
   */
  async subscribeToTopic(topic: string) {
    try {
      await this.http.post(`${this.apiUrl}/notifications/topics/subscribe`, { topic }).toPromise();
      console.log(`Subscribed to topic: ${topic}`);
    } catch (error) {
      console.error('Error subscribing to topic:', error);
    }
  }

  /**
   * Unsubscribe from topic
   */
  async unsubscribeFromTopic(topic: string) {
    try {
      await this.http.delete(`${this.apiUrl}/notifications/topics/unsubscribe`, { 
        body: { topic } 
      }).toPromise();
      console.log(`Unsubscribed from topic: ${topic}`);
    } catch (error) {
      console.error('Error unsubscribing from topic:', error);
    }
  }

  /**
   * Get notification history from server
   */
  async getServerNotificationHistory(page = 1, limit = 20) {
    try {
      const response = await this.http.get(`${this.apiUrl}/notifications/history`, {
        params: {
          page: page.toString(),
          limit: limit.toString()
        }
      }).toPromise();
      return response;
    } catch (error) {
      console.error('Error getting notification history:', error);
      return { docs: [], total: 0 };
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string) {
    try {
      await this.http.put(`${this.apiUrl}/notifications/${notificationId}/read`, {}).toPromise();
      console.log(`Notification ${notificationId} marked as read`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  /**
   * Cleanup - remove listeners
   */
  async cleanup() {
    await PushNotifications.removeAllListeners();
    this.isInitialized = false;
    console.log('Push notification listeners removed');
  }

  /**
   * Check if push notifications are supported
   */
  isSupported(): boolean {
    return this.platform.is('capacitor') && (this.platform.is('ios') || this.platform.is('android'));
  }

  /**
   * Test notification (for development)
   */
  async sendTestNotification() {
    if (!this.isSupported()) {
      const toast = await this.toastController.create({
        message: 'Push notifications are only available on mobile devices',
        duration: 3000,
        position: 'bottom'
      });
      await toast.present();
      return;
    }

    try {
      await this.http.post(`${this.apiUrl}/notifications/test`, {}).toPromise();
      
      const toast = await this.toastController.create({
        message: 'Test notification sent! You should receive it shortly.',
        duration: 3000,
        position: 'bottom',
        color: 'success'
      });
      await toast.present();
    } catch (error) {
      console.error('Error sending test notification:', error);
      
      const toast = await this.toastController.create({
        message: 'Failed to send test notification',
        duration: 3000,
        position: 'bottom',
        color: 'danger'
      });
      await toast.present();
    }
  }
}